import { getEndCursor } from 'components/Blocks/Pagination/paginationHelpers';
import Breadcrumbs from 'components/Layout/Breadcrumbs/Breadcrumbs';
import CommonLayout from 'components/Layout/CommonLayout';
import { Webline } from 'components/Layout/Webline/Webline';
import ProductDetail from 'components/Pages/ProductDetail/ProductDetail';
import { mapProductDetailApiData, mapVariantDetailApiData } from 'connectors/products/ProductDetail';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import {
    MainVariantDetailFragmentApi,
    ProductDetailQueryApi,
    ProductDetailQueryDocumentApi,
    ProductDetailQueryVariablesApi,
    ReviewQueryDocumentApi,
    ReviewQueryVariablesApi,
    useProductDetailQueryApi,
    VariantDetailFragmentApi,
} from 'graphql/generated';
import { getDefaultStoreUuidCookieOnSsr } from 'helpers/cookies/defaultStoreCookieHelper';
import { createClient } from 'helpers/createClient';
import { initServerSideProps, QueriesArray, ServerSidePropsType } from 'helpers/InitServerSideProps';
import { getIsRedirectedFromSsr } from 'helpers/isServer';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { getSeoTitleAndDescriptionForFriendlyUrlPage } from 'helpers/seo/getSeoTitleAndDescriptionForFriendlyUrlPage';
import { getServerSidePropsWrapper } from 'helpers/serverSide/getServerSidePropsWrapper';
import { handleServerSideErrorResponseForFriendlyUrls } from 'helpers/serverSide/handleServerSideErrorResponseForFriendlyUrls';
import { useQueryError } from 'hooks/graphQl/UseQueryError';
import { useGtmFriendlyPageView } from 'hooks/gtm/useGtmFriendlyPageView';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { NextPageExtended } from 'types/global';
import { REVIEWS_DEFAULT_PAGE_SIZE } from 'types/review';
import { CombinedError, OperationResult } from 'urql';
import { getCatNumFromServerSideUrl, getSlugFromServerSideUrl, getSlugFromUrl } from 'utils/getSlugFromUrl';
import { useGtmPageViewEvent } from 'utils/Gtm/EventFactories';
import { getGtmPageInfoForFriendlyUrl } from 'utils/Gtm/Gtm';

type Props = ServerSidePropsType & {
    defaultStoreUuidFromSSR: string | null;
};

const ProductDetailPage: NextPageExtended<Props> = ({ defaultStoreUuidFromSSR }) => {
    const router = useRouter();
    const [shouldRefetch, setShouldRefetch] = useState(false);
    const slug = getUrlWithoutGetParameters(router.asPath);
    const catNumFromUrl = getCatNumFromServerSideUrl(router.asPath);
    const currentDomainConfig = useDomainConfig();
    const storeUuid = usePersistStore((s) => s.defaultStoreUuid);

    const [{ data: productData, fetching, error }, refetch] = useProductDetailQueryApi({
        variables: {
            urlSlug: catNumFromUrl ? null : getSlugFromUrl(slug),
            defaultStoreUuid: storeUuid ?? defaultStoreUuidFromSSR ?? null,
            catNum: catNumFromUrl,
        },
    });

    useQueryError(error, error instanceof CombinedError);

    const mappedProduct = useMemo(() => {
        if (!productData?.product) {
            return null;
        }

        if (productData.product.__typename === 'RegularProduct') {
            return mapProductDetailApiData(productData.product, currentDomainConfig.currencyCode);
        }

        return mapVariantDetailApiData(
            productData.product as MainVariantDetailFragmentApi | VariantDetailFragmentApi,
            currentDomainConfig.currencyCode,
        );
    }, [currentDomainConfig.currencyCode, productData?.product]);

    const productSeoTitleAndDescription = mappedProduct
        ? getSeoTitleAndDescriptionForFriendlyUrlPage(mappedProduct)
        : undefined;

    const gtmProductUrlPageViewEvent = useGtmPageViewEvent(
        getGtmPageInfoForFriendlyUrl(mappedProduct, slug, mappedProduct?.breadcrumb),
    );
    useGtmFriendlyPageView(gtmProductUrlPageViewEvent, slug, fetching);

    const productMainImage = mappedProduct?.mainImage?.url ?? undefined;

    useBroadcastChannel('orderCompleteBroadcast', () => {
        setShouldRefetch(true);
    });

    useEffect(() => {
        if (shouldRefetch) {
            refetch({ requestPolicy: 'network-only' });
            setShouldRefetch(false);
        }
    }, [shouldRefetch, refetch]);

    return (
        <CommonLayout
            showLastVisitedProducts
            description={productSeoTitleAndDescription?.description}
            hreflangLinks={productData?.product.hreflangLinks}
            isFetchingData={fetching}
            ogImageUrlDefault={productMainImage}
            title={productSeoTitleAndDescription?.title}
        >
            {mappedProduct?.breadcrumb && (
                <Webline>
                    <Breadcrumbs key="breadcrumb" breadcrumb={mappedProduct.breadcrumb} type="category" />
                </Webline>
            )}
            {mappedProduct && <ProductDetail fetching={fetching} product={mappedProduct} />}
        </CommonLayout>
    );
};

ProductDetailPage.layout = 'plain';

export const getServerSideProps = getServerSidePropsWrapper(
    ({ domainConfig, redisClient, ssrExchange, t }) =>
        async (context) => {
            const client = createClient({
                publicGraphqlEndpoint: domainConfig.publicGraphqlEndpoint,
                ssrExchange,
                t,
                context,
                redisClient,
            });

            const catNumFromUrl = getCatNumFromServerSideUrl(context.req.url ?? '');
            const defaultStoreUuidFromSSR = getDefaultStoreUuidCookieOnSsr(context);

            const productResponse: OperationResult<ProductDetailQueryApi, ProductDetailQueryVariablesApi> = await client
                .query(ProductDetailQueryDocumentApi, {
                    urlSlug: catNumFromUrl
                        ? null
                        : getSlugFromServerSideUrl(context.req.url ?? '', context.req.headers),
                    catNum: catNumFromUrl,
                    defaultStoreUuid: defaultStoreUuidFromSSR,
                })
                .toPromise();

            const hasFatalProductError =
                !productResponse.data?.product &&
                productResponse.error?.graphQLErrors.some(({ extensions }) => Number(extensions.code) === 500);

            if (hasFatalProductError) {
                throw new Error('Internal Server Error');
            }

            const queriesToPrefetch: QueriesArray<ReviewQueryVariablesApi> = [];

            const video = context.req.url?.includes('video') ? '#video' : '';

            if (catNumFromUrl && productResponse.data?.product) {
                return {
                    redirect: {
                        destination: '/' + productResponse.data.product.slug + video,
                        statusCode: 301,
                    },
                };
            }

            if (productResponse.data?.product.uuid) {
                queriesToPrefetch.push({
                    query: ReviewQueryDocumentApi,
                    variables: {
                        productUuid: productResponse.data.product.uuid,
                        after: getEndCursor(0, 0, REVIEWS_DEFAULT_PAGE_SIZE),
                        first: REVIEWS_DEFAULT_PAGE_SIZE,
                    },
                });
            }

            if (getIsRedirectedFromSsr(context.req.headers)) {
                const serverSideErrorResponse = handleServerSideErrorResponseForFriendlyUrls(
                    productResponse.error?.graphQLErrors,
                    productResponse.data?.product,
                    context.res,
                );

                if (serverSideErrorResponse) {
                    return serverSideErrorResponse;
                }
            }

            const initServerSideData = await initServerSideProps<ReviewQueryVariablesApi>({
                context,
                domainConfig,
                client,
                ssrExchange,
                prefetchedQueries: queriesToPrefetch,
            });

            return {
                ...initServerSideData,
                props: {
                    ...('props' in initServerSideData && typeof initServerSideData.props === 'object'
                        ? initServerSideData.props
                        : {}),
                    defaultStoreUuidFromSSR,
                },
            };
        },
);

export default ProductDetailPage;
